import React from 'react';
import { Container, Box, Typography, Paper, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

const GradientBackground = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #F9FAFB 0%, #EFF6FF 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '70%',
    height: '100%',
    background: 'var(--primary-gradient)',
    clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 20% 0)',
    opacity: 0.05,
    zIndex: 0,
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: '2.5rem',
  textAlign: 'center',
}));

const AuthCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '1.5rem',
  padding: '3rem',
  width: '100%',
  maxWidth: '500px',
  boxShadow: '0 20px 25px rgba(0, 0, 0, 0.05), 0 10px 10px rgba(0, 0, 0, 0.02)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: '2rem',
  },
}));

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
      duration: 0.5
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { 
      type: "spring", 
      stiffness: 300, 
      damping: 24 
    }
  }
};

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <GradientBackground>
      <Container maxWidth="sm">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AuthCard elevation={0}>
            <motion.div variants={itemVariants}>
              <LogoContainer>
                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight={800}
                  sx={{ 
                    mb: 1,
                    background: 'var(--primary-gradient)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img 
                      src="/assets/akmobooks.com.svg" 
                      alt="AkMo Books Logo" 
                      style={{ height: '23px', width: 'auto' }} 
                    />
                    AkMo Books
                  </Box>
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  Accounting Services On Your Go!
                </Typography>
              </LogoContainer>
            </motion.div>
            <motion.div variants={itemVariants}>
              {children}
            </motion.div>
          </AuthCard>
        </motion.div>
      </Container>
    </GradientBackground>
  );
};

export default AuthLayout; 